import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'react-modal';
import { GetAllCategoryFilterMasterforWeb, GetAllPropertyforWeb, fnGetFilteredPropertiesusingLocationName } from '../../APIs/APIHelperforWeb';
import './LocationSearch.css';

const parseStoredJson = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key)) || [];
  } catch (error) {
    return [];
  }
};

const toShortName = (value) => {
  const words = (value || '')
    .toString()
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!words.length) return '';

  return words
    .map((word) => word[0])
    .join('')
    .toUpperCase();
};

const toSearchText = (value) => (value || '').toString().trim().toLowerCase();

const getPropertyLocationText = (property) => (
  property?.CityName
  || property?.City
  || property?.StateName
  || property?.State
  || property?.AreaIncluding
  || property?.Area
  || ''
).toString().trim();

const KeywordSearch = ({ isOpen, onClose, onSelect, LocationName }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('All');
  const [propertyTypes, setPropertyTypes] = useState([]);

  const buildPropertyTypes = (rows) => {
    const seen = new Set();

    const uniqueItems = (rows || [])
      .filter((item) => item && item.IsActive === true)
      .map((item) => {
        // If item looks like a property object, map to property fields
        if (item.PropertyTypeName || item.PropertyLabelName || item.PropertyTitle) {
          return {
            name: (item.PropertyTypeName || item.PropertyLabelName || item.PropertyTitle || '').toString().trim(),
            group: (item.BuildingTypeName || item.PropertyCategory || '').toString().trim(),
            shortname: toShortName(item.PropertyTypeName || item.PropertyLabelName || item.PropertyTitle),
          };
        }

        // Otherwise assume CategoryFilterMaster / section rows
        return {
          name: (item.ChildName || item.SectionName || '').toString().trim(),
          group: (item.ParentName || '').toString().trim(),
          shortname: toShortName(item.ChildName || item.SectionName),
        };
      })
      .filter((item) => {
        if (!item.name) return false;
        const key = item.name.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

    return uniqueItems;
  };

  useEffect(() => {
    const loadPropertyTypes = async () => {
      const locationQuery = toSearchText(LocationName);

      if (locationQuery) {
        let properties = parseStoredJson('AllPropertiesforWeb');
        properties = Array.isArray(properties) ? properties : [];

        if (!properties.length) {
          const apiRows = await GetAllPropertyforWeb();
          properties = Array.isArray(apiRows) && apiRows.length > 0 ? apiRows : parseStoredJson('AllPropertiesforWeb');
        }

        const matchedProperties = fnGetFilteredPropertiesusingLocationName(LocationName, properties);
        setPropertyTypes(buildPropertyTypes(matchedProperties));
        return;
      }

      let filterRows = parseStoredJson('CategoryFilterMasterListforWeb');
      filterRows = Array.isArray(filterRows) ? filterRows.filter((row) => row && row.IsActive === true) : [];
      if (!Array.isArray(filterRows) || filterRows.length === 0) {
        const apiRows = await GetAllCategoryFilterMasterforWeb();
        filterRows = Array.isArray(apiRows) && apiRows.length > 0
          ? apiRows.filter((row) => row && row.IsActive === true)
          : parseStoredJson('CategoryFilterMasterListforWeb');
      }

      setPropertyTypes(buildPropertyTypes(filterRows));
    };

    loadPropertyTypes();
  }, [LocationName]);

  const typeFilters = useMemo(() => {
    const filters = Array.from(new Set(propertyTypes.map((item) => item.group)));
    return ['All', ...filters];
  }, [propertyTypes]);

  const normalizedQuery = searchQuery.trim().toLowerCase();

  const filteredPropertyTypes = propertyTypes.filter((item) => {
    const matchesType = selectedType === 'All' || item.group === selectedType;
    const matchesQuery = !normalizedQuery || item.name.toLowerCase().includes(normalizedQuery);
    return matchesType && matchesQuery;
  });

  const handleSelect = (propertyTypeName) => {
    onSelect(propertyTypeName);
    setSearchQuery('');
    setSelectedType('All');
    onClose();
  };

  const handleClose = () => {
    setSearchQuery('');
    setSelectedType('All');
    onClose();
  };




  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      closeTimeoutMS={500}
      style={{
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
        },
      }}
      className={{
        base: 'location-modal-content',
        afterOpen: 'location-modal-content-open',
        beforeClose: 'location-modal-content-close',
      }}
      overlayClassName={{
        base: 'location-modal-overlay',
        afterOpen: 'location-modal-overlay-open',
        beforeClose: 'location-modal-overlay-close',
      }}
      ariaHideApp={false}
    >
      <div className="location-modal-theme" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #dbe3ef', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 className="location-modal-title" style={{ margin: 0 }}>Select Property Type</h2>
          <button onClick={handleClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#64748b' }}>x</button>
        </div>

        <div style={{ padding: '16px 24px 12px' }}>
          <input
            type="text"
            placeholder="Search property type"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            className="location-modal-input"
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #cbd5e1',
              borderRadius: '12px',
              background: '#ffffff',
              boxSizing: 'border-box',
            }}
          />
        </div>

        <div style={{ padding: '0 24px 12px', overflowX: 'auto', display: 'flex', gap: '8px' }}>
          {typeFilters.map((filter) => (
            <button
              key={filter}
              type="button"
              onClick={() => setSelectedType(filter)}
              className="location-modal-chip"
              style={{
                border: 'none',
                borderRadius: '999px',
                padding: '9px 16px',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                color: selectedType === filter ? '#ffffff' : '#334155',
                background: selectedType === filter ? '#4338ca' : '#e5e7eb',
              }}
            >
              {filter}
            </button>
          ))}
        </div>

        <div className="location-modal-scrollable" style={{ flex: 1, overflowY: 'auto', padding: '0 24px 20px' }}>
          {filteredPropertyTypes.length > 0 ? (
            <div className="location-type-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px' }}>
              {filteredPropertyTypes.map((item) => (
                <button
                  key={item.name}
                  type="button"
                  onClick={() => handleSelect(item.name)}
                  style={{
                    border: '1px solid #dbe3ef',
                    borderRadius: '14px',
                    background: '#ffffff',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '10px',
                    cursor: 'pointer',
                  }}
                >
                  <div
                    style={{
                      width: '54px',
                      height: '54px',
                      borderRadius: '10px',
                      background: '#e2e8f0',
                      color: '#334155',
                      fontSize: '14px',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {item.shortname || 'NA'}
                  </div>
                  <div className="location-type-text">
                    <div className="location-type-name" style={{ color: '#1e293b', fontSize: '16px', fontWeight: 600, lineHeight: 1.3 }}>{item.name}</div>
                    <div className="location-type-group" style={{ color: '#64748b', fontSize: '12px', fontWeight: 500, marginTop: '4px' }}>{item.group}</div>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="location-no-results">No matching property type found</div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default KeywordSearch;
